import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {is_mobile} from './utils.js';

// ─────────────────────────────────────────────────────────────────────────────
// Horizontal scroll generico (desktop).
//
// Markup atteso (default):
//   <div class="js-h-outer">
//       <div class="js-h-scroll">
//           <section class="js-h-panel">…</section>
//           …
//       </div>
//       <img class="hs__scroll-global" … />   (opzionale)
//   </div>
//
// Selettori e fasi sono configurabili: about.js e lab.js lo usano con plan
// custom (dwell sulla timeline / wordPanel) per evitare duplicazione del
// pin+timeline+ScrollTrigger.
//
// Opzioni:
//   outer, wrapper, panels        — selettore o elemento (panels: anche array)
//   transitionScroll, holdScroll  — durate base (px) delle fasi auto-generate
//   dwells                        — { panelIndex: lengthPx } per fermarsi su un pannello
//   phases                        — fn|array per sovrascrivere il piano fasi
//   getPanelX                     — (panel, i) => xOffset (default: -vw * i)
//   onUpdate                      — (self, ctx) ctx = {phase, localProg, phases, panels}
//   snapTo                        — (value, phases) => snapValue
//   snapDuration, snapEase, scrub — passati a ScrollTrigger.snap
//   useGenericMobile              — true: usa swipe nav generico; false: niente (caller gestisce)
//   mobileBreakpoint              — default 992
//   startBtn, startBtnTarget      — bottone "salta alla prima sezione utile"
//   scrollSvg                     — selettore svg da nascondere all'arrivo sull'ultimo pannello
//   skipIf                        — fn() => bool: se true, init torna null
//   lenis                         — istanza lenis (default window.lenis)
//
// Ritorna: { st, phases, rebuild } oppure null.
// ─────────────────────────────────────────────────────────────────────────────
function initHorizontalScroll(opts = {}) {
    if (typeof opts.skipIf === 'function' && opts.skipIf()) {
        return null;
    }

    const outer = resolveEl(opts.outer || '.js-h-outer');
    if (!outer) {
        return null;
    }
    const wrapper = resolveEl(opts.wrapper || '.js-h-scroll', outer);
    if (!wrapper) {
        return null;
    }
    const panels = resolvePanels(opts.panels, wrapper);
    if (!panels.length) {
        return null;
    }

    const scrollSvg = opts.scrollSvg === null
        ? null
        : (document.querySelector(opts.scrollSvg || '.hs__scroll-global')
            || document.querySelector(opts.scrollSvg || '.hs__scroll-global'));

    const gotoStartBtn = opts.gotoStartBtn === null
        ? null
        : outer.querySelector(opts.gotoStartBtn || '.hs__goto-start');

    const startBtn = opts.startBtn === null
        ? null
        : document.querySelector(opts.startBtn || '.js-start-horizontal');

    const MOBILE_BP = opts.mobileBreakpoint || 992;
    const TRANS_SCROLL = opts.transitionScroll != null ? opts.transitionScroll : (is_mobile() ? 700 : 1400);
    const HOLD_SCROLL = opts.holdScroll != null ? opts.holdScroll : (is_mobile() ? 700 : 1400);
    const lenis = opts.lenis || (typeof window !== 'undefined' ? window.lenis : null);

    let st = null, tl = null;
    let mobileNav = null;
    let currentPhases = [];
    let desktopPinned = false;
    let wheelHijack = null;

    function defaultPhases() {
        const dwells = opts.dwells || {};
        const list = [];
        for (let i = 0; i < panels.length; i++) {
            if (dwells[i]) {
                list.push({type: 'dwell', panelIndex: i, len: dwells[i]});
            }
            if (i < panels.length - 1) {
                list.push({type: 'transition', fromIndex: i, toIndex: i + 1, len: TRANS_SCROLL});
            }
        }
        if (HOLD_SCROLL > 0) {
            list.push({type: 'hold', panelIndex: panels.length - 1, len: HOLD_SCROLL});
        }
        return list;
    }

    function panelX(i, vw) {
        if (typeof opts.getPanelX === 'function') {
            return opts.getPanelX(panels[i], i);
        }
        return -vw * i;
    }

    function annotatePhases(list) {
        const total = list.reduce((s, p) => s + p.len, 0);
        let cum = 0;
        list.forEach(p => {
            p.f0 = total > 0 ? cum / total : 0;
            cum += p.len;
            p.f1 = total > 0 ? cum / total : 1;
        });
        return total;
    }

    function findActivePhase(progress, list) {
        for (let i = 0; i < list.length; i++) {
            if (progress < list[i].f1) {
                return list[i];
            }
        }
        return list[list.length - 1];
    }

    function defaultSnapTo(value, list) {
        // Nessuno snap dentro le fasi di dwell (scroll libero per contenuti interni)
        const inDwell = list.some(p => p.type === 'dwell' && value > p.f0 + 0.001 && value < p.f1 - 0.001);
        if (inDwell) {
            return value;
        }
        const bounds = list.map(p => p.f0);
        return bounds.reduce((a, b) => Math.abs(b - value) < Math.abs(a - value) ? b : a);
    }

    function arriveLastPanelFrac(list) {
        // Frazione in cui il wrapper raggiunge l'ultimo pannello (per nascondere svg scroll)
        for (let i = list.length - 1; i >= 0; i--) {
            const p = list[i];
            if (p.type === 'transition' && p.toIndex === panels.length - 1) {
                return p.f1;
            }
        }
        return list.length ? list[list.length - 1].f0 : 1;
    }

    function buildDesktop() {
        gsap.set(wrapper, {clearProps: 'x'});

        const vw = window.innerWidth;
        const built = typeof opts.phases === 'function'
            ? opts.phases({panels, vw, TRANS_SCROLL, HOLD_SCROLL})
            : (opts.phases || defaultPhases());
        const totalScroll = annotatePhases(built);
        currentPhases = built;
        if (totalScroll <= 0) {
            return null;
        }

        tl = gsap.timeline();
        built.forEach(p => {
            const targetIndex = p.type === 'transition' ? p.toIndex : p.panelIndex;
            tl.to(wrapper, {
                x: () => panelX(targetIndex, window.innerWidth),
                ease: 'none',
                duration: p.f1 - p.f0
            });
        });

        const lastBoundary = arriveLastPanelFrac(built);

        const snapTo = typeof opts.snapTo === 'function'
            ? (value) => opts.snapTo(value, built)
            : (value) => defaultSnapTo(value, built);

        st = ScrollTrigger.create({
            trigger: outer,
            start: 'top top',
            end: '+=' + totalScroll,
            pin: true,
            anticipatePin: 1,
            animation: tl,
            scrub: opts.scrub != null ? opts.scrub : 1,
            invalidateOnRefresh: true,
            snap: {
                snapTo: snapTo,
                directional: true,
                duration: opts.snapDuration || {min: 0.2, max: 0.5},
                delay: opts.snapDelay != null ? opts.snapDelay : 0.05,
                inertia: opts.snapInertia != null ? opts.snapInertia : false,
                ease: opts.snapEase || 'power2.inOut'
            },
            onUpdate(self) {
                if (scrollSvg) {
                    scrollSvg.classList.toggle('--hidden', self.progress >= lastBoundary);
                }
                if (gotoStartBtn) {
                    gotoStartBtn.classList.toggle('active', self.progress >= lastBoundary);
                }
                if (typeof opts.onUpdate === 'function') {
                    const phase = findActivePhase(self.progress, built);
                    const span = (phase.f1 - phase.f0) || 1;
                    const localProg = Math.min(Math.max((self.progress - phase.f0) / span, 0), 1);
                    opts.onUpdate(self, {phase, localProg, phases: built, panels});
                }
            }
        });

        window.horizontalScrollST = st;

        installWheelHijack(built, totalScroll);
    }

    // ── Hijack wheel desktop: 1 gesto = 1 sezione ────────────────────────────
    function installWheelHijack(list, totalScroll) {
        uninstallWheelHijack();
        if (opts.wheelHijack === false) {
            return null;
        }

        const DEBOUNCE = opts.wheelDebounce != null ? opts.wheelDebounce : 120;
        const SCROLL_DURATION = opts.wheelScrollDuration != null ? opts.wheelScrollDuration : 1.0;
        let busy = false;
        let lastTime = 0;

        const boundaries = list.map(p => st.start + p.f0 * totalScroll);
        boundaries.push(st.end);

        function getScrollY() {
            return lenis && typeof lenis.scroll === 'number' ? lenis.scroll : window.scrollY;
        }

        function onWheel(e) {
            if (!st || !st.isActive) {
                return null;
            }
            if (busy) {
                e.preventDefault();
                return null;
            }
            const now = performance.now();
            if (now - lastTime < DEBOUNCE) {
                e.preventDefault();
                return null;
            }

            const scrollY = getScrollY();
            const progress = totalScroll > 0 ? (scrollY - st.start) / totalScroll : 0;
            const phase = findActivePhase(Math.min(Math.max(progress, 0), 1), list);
            const forward = e.deltaY > 0;

            if (phase && phase.type === 'dwell') {
                const eps = 0.002;
                if (forward && progress < phase.f1 - eps) {
                    return null;
                }
                if (!forward && progress > phase.f0 + eps) {
                    return null;
                }
            }

            let target = null;
            if (forward) {
                for (let i = 0; i < boundaries.length; i++) {
                    if (boundaries[i] > scrollY + 1) {
                        target = boundaries[i];
                        break;
                    }
                }
            } else {
                for (let i = boundaries.length - 1; i >= 0; i--) {
                    if (boundaries[i] < scrollY - 1) {
                        target = boundaries[i];
                        break;
                    }
                }
            }
            if (target == null) {
                return null;
            }

            e.preventDefault();
            lastTime = now;
            busy = true;

            const release = () => {
                busy = false;
            };

            if (lenis && typeof lenis.scrollTo === 'function') {
                lenis.scrollTo(target, {
                    duration: SCROLL_DURATION,
                    lock: true,
                    onComplete: release
                });
            } else {
                window.scrollTo({top: target, behavior: 'smooth'});
                setTimeout(release, SCROLL_DURATION * 1000);
            }
        }

        window.addEventListener('wheel', onWheel, {passive: false});
        wheelHijack = onWheel;
    }

    function uninstallWheelHijack() {
        if (!wheelHijack) {
            return null;
        }
        window.removeEventListener('wheel', wheelHijack, {passive: false});
        wheelHijack = null;
    }

    // ── Mobile nav generico: swipe verticale → cambia sezione ────────────────
    function buildMobileNav() {
        if (mobileNav) {
            return null;
        }
        let sectionIndex = 0;
        let isAtFooter = false;

        function onScroll() {
            isAtFooter = window.scrollY > outer.offsetHeight * 0.3;
            if (scrollSvg) {
                scrollSvg.classList.toggle('--hidden', isAtFooter || sectionIndex === panels.length - 1);
            }
        }
        window.addEventListener('scroll', onScroll, {passive: true});
        onScroll();

        function goToSection(i, animate) {
            sectionIndex = Math.max(0, Math.min(i, panels.length - 1));
            wrapper.style.transition = animate === false ? 'none' : '';
            wrapper.style.transform = 'translate3d(' + (-sectionIndex * window.innerWidth) + 'px,0,0)';
            const isLast = sectionIndex === panels.length - 1;
            outer.classList.toggle('is-last-panel', isLast);
            if (gotoStartBtn) {
                gotoStartBtn.classList.toggle('active', isLast);
            }
            if (scrollSvg) {
                scrollSvg.classList.toggle('--hidden', isLast || isAtFooter);
            }
        }

        let winStartY = 0, winStartX = 0, winGesture = null;
        let dragActive = false, dragBase = 0;
        let lastDy = 0, lastT = 0, velocity = 0;

        function snapBack() {
            if (dragActive) {
                goToSection(sectionIndex, true);
            }
        }

        function onWinStart(e) {
            winStartY = e.touches[0].clientY;
            winStartX = e.touches[0].clientX;
            winGesture = null;
            dragActive = false;
            lastDy = 0;
            velocity = 0;
            lastT = e.timeStamp || performance.now();
        }

        function onWinMove(e) {
            const dy = e.touches[0].clientY - winStartY;
            const dx = e.touches[0].clientX - winStartX;
            if (winGesture === null && (Math.abs(dy) > 6 || Math.abs(dx) > 6)) {
                if (Math.abs(dy) < Math.abs(dx)) {
                    winGesture = 'pass';
                    return;
                }
                const fwd = dy < 0;
                const canAct = fwd
                    ? (!isAtFooter && sectionIndex < panels.length - 1)
                    : (!isAtFooter && sectionIndex > 0);
                winGesture = canAct ? 'hijack' : 'pass';
                if (winGesture === 'hijack') {
                    dragActive = true;
                    dragBase = -sectionIndex * window.innerWidth;
                    wrapper.style.transition = 'none';
                }
            }
            if (winGesture === 'hijack' && dragActive) {
                e.preventDefault();
                e.stopPropagation();
                const now = e.timeStamp || performance.now();
                const dt = now - lastT;
                if (dt > 0) {
                    velocity = (dy - lastDy) / dt;
                }
                lastDy = dy;
                lastT = now;
                let offset = dy;
                if ((dragBase + offset) > 0) {
                    offset = offset * 0.3;
                }
                wrapper.style.transform = 'translate3d(' + (dragBase + offset) + 'px,0,0)';
            }
        }

        function onWinEnd(e) {
            if (winGesture !== 'hijack' || !dragActive) {
                winGesture = null;
                dragActive = false;
                return null;
            }
            const dy = e.changedTouches[0].clientY - winStartY;
            const vw = window.innerWidth;
            const distThresh = vw * 0.18;
            const velThresh = 0.4;
            const goNext = dy < -distThresh || velocity < -velThresh;
            const goPrev = dy > distThresh || velocity > velThresh;

            wrapper.style.transition = '';

            if (goNext && sectionIndex < panels.length - 1) {
                goToSection(sectionIndex + 1, true);
            } else if (goPrev && sectionIndex > 0) {
                goToSection(sectionIndex - 1, true);
            } else {
                snapBack();
            }

            winGesture = null;
            dragActive = false;
        }

        window.addEventListener('touchstart', onWinStart, {passive: true});
        window.addEventListener('touchmove', onWinMove, {passive: false});
        window.addEventListener('touchend', onWinEnd, {passive: true});

        mobileNav = {
            onWinStart, onWinMove, onWinEnd, onScroll, goToSection,
            refresh: () => goToSection(sectionIndex, false)
        };
    }

    function destroyMobileNav() {
        if (!mobileNav) {
            return null;
        }
        window.removeEventListener('touchstart', mobileNav.onWinStart);
        window.removeEventListener('touchmove', mobileNav.onWinMove);
        window.removeEventListener('touchend', mobileNav.onWinEnd);
        window.removeEventListener('scroll', mobileNav.onScroll);
        wrapper.style.transform = '';
        outer.classList.remove('is-last-panel');
        mobileNav = null;
    }

    function build() {
        uninstallWheelHijack();
        if (st) {
            st.kill();
            st = null;
        }
        if (tl) {
            tl.kill();
            tl = null;
        }
        const isMobileLayout = window.innerWidth < MOBILE_BP;
        const useGenericMobile = opts.useGenericMobile !== false;

        if (isMobileLayout) {
            if (desktopPinned) {
                gsap.set(wrapper, {clearProps: 'x'});
                desktopPinned = false;
            }
            if (mobileNav) {
                mobileNav.refresh();
            } else if (useGenericMobile) {
                buildMobileNav();
            }
            return null;
        }

        destroyMobileNav();
        buildDesktop();
        desktopPinned = true;
    }

    build();

    if (startBtn) {
        startBtn.addEventListener('click', () => {
            if (!st) {
                return null;
            }
            const target = typeof opts.startBtnTarget === 'function'
                ? opts.startBtnTarget(st, currentPhases)
                : st.start + (currentPhases[0] ? currentPhases[0].len : TRANS_SCROLL);
            if (lenis) {
                lenis.scrollTo(target, {duration: 1.4, lerp: 0.08});
            }
        });
    }

    if (gotoStartBtn) {
        gotoStartBtn.addEventListener('click', () => {
            if (mobileNav) {
                mobileNav.goToSection(0, true);
            }
            if (lenis) {
                lenis.scrollTo(0, {duration: 1.4, lerp: 0.08});
            } else {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
        });
    }

    window.addEventListener('resize', () => {
        build();
        ScrollTrigger.refresh();
    });

    return {
        get st() {
            return st;
        },
        get phases() {
            return currentPhases;
        },
        rebuild: build
    };
}

function resolveEl(input, root) {
    if (!input) {
        return null;
    }
    if (typeof input === 'string') {
        return (root || document).querySelector(input);
    }
    return input;
}

function resolvePanels(input, wrapper) {
    if (Array.isArray(input)) {
        return input;
    }
    const selector = (typeof input === 'string' && input) || '.js-h-panel';
    return Array.from(wrapper.querySelectorAll(`:scope > ${selector}`));
}

export {initHorizontalScroll};
